// src/pages/inventory/purchase-orders/PurchaseOrdersTab.tsx
import React, { useState, useEffect } from "react";
import { Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import { DataTable, PageToolbar, StatusTag } from "../../../../components/shared";
import { fetchPurchaseOrders } from "../../../../api/inventory/purchaseOrderApi";
import type { PurchaseOrder } from "../../../../types/inventory/OrderTypes";
import EditPurchaseOrderDrawer from "./drawers/EditPurchaseOrderDrawer";
import { useNotifications } from "../../../../context/NotificationContext";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

const defaultPurchaseOrder: PurchaseOrder = {
	supplier_id: 0,
	order_date: new Date().toISOString(),
	status: "pending",
	notes: "",
	items: [],
};

const PurchaseOrdersTab: React.FC = () => {
	const [orders, setOrders] = useState<PurchaseOrder[]>([]);
	const [totalOrders, setTotalOrders] = useState<number>(0);
	const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>("");
	const { showError } = useNotifications();
	const ORDERS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	useEffect(() => {
		loadOrders();
	}, [currentPage]);

	const loadOrders = async () => {
		setLoading(true);
		try {
			const { data, total } = await fetchPurchaseOrders({
				page: currentPage,
				per_page: ORDERS_PER_PAGE,
			});
			setOrders(data);
			setTotalOrders(total);
		} catch (error) {
			showError("Failed to load purchase orders");
		} finally {
			setLoading(false);
		}
	};

	const handleNewOrder = () => {
		setSelectedOrder(defaultPurchaseOrder);
		setDrawerOpen(true);
	};

	const handleEditOrder = (order: PurchaseOrder) => {
		setSelectedOrder(order);
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setSelectedOrder(null);
		setDrawerOpen(false);
		loadOrders();
	};

	const filteredOrders = searchValue
		? orders.filter(
			(o) =>
				String(o.purchase_order_id).includes(searchValue) ||
				o.notes?.toLowerCase().includes(searchValue.toLowerCase()) ||
				o.status?.toLowerCase().includes(searchValue.toLowerCase())
		)
		: orders;

	const columns: ColumnsType<PurchaseOrder> = [
		{
			title: "Order ID",
			dataIndex: "purchase_order_id",
			key: "purchase_order_id",
			width: 120,
			render: (id: number) => <Text strong>PO-{String(id).padStart(4, "0")}</Text>,
		},
		{
			title: "Supplier",
			dataIndex: "supplier_id",
			key: "supplier_id",
			width: 120,
		},
		{
			title: "Order Date",
			dataIndex: "order_date",
			key: "order_date",
			width: 130,
			render: (date: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 120,
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Total",
			dataIndex: "total_amount",
			key: "total_amount",
			align: "right",
			width: 130,
			render: (value: number) => value ? <Text strong>${value.toFixed(2)}</Text> : <Text type="secondary">—</Text>,
		},
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			ellipsis: true,
			render: (text: string) => <Text type="secondary">{text || "—"}</Text>,
		},
	];

	return (
		<>
			<PageToolbar
				primaryAction={{ label: "New Purchase Order", icon: <PlusOutlined />, onClick: handleNewOrder }}
				extraActions={[{ label: "Refresh", icon: <ReloadOutlined />, onClick: loadOrders }]}
				filters={[
					{
						type: "search",
						placeholder: "Search orders…",
						value: searchValue,
						onChange: (v) => setSearchValue(v),
						width: 260,
					},
				]}
				onReset={() => setSearchValue("")}
			/>
			<DataTable
				bare
				columns={columns}
				dataSource={filteredOrders}
				rowKey="purchase_order_id"
				loading={loading}
				total={totalOrders}
				currentPage={currentPage}
				pageSize={ORDERS_PER_PAGE}
				onPageChange={(page) => setCurrentPage(page)}
				onRowClick={handleEditOrder}
				emptyTitle="No purchase orders found"
				emptyDescription="Create your first purchase order to get started"
				emptyAction={{ label: "New Purchase Order", onClick: handleNewOrder }}
			/>

			{selectedOrder && <EditPurchaseOrderDrawer isOpen={drawerOpen} onClose={handleDrawerClose} order={selectedOrder} />}
		</>
	);
};

export default PurchaseOrdersTab;
